// @givmo/compliance — a filing the review put on the calendar says so on its panel (CCM-R1-REVIEW-
// WEB; plan §1.8 item 5: an assessment-derived filing keeps its own provenance, never mistaken for
// one the organization added itself).
//
// N41: "Added to your calendar from your review on {date}, by {Name}." A connected AI's review
// records the person as the confirmer, while the AI's part is on the review itself (`channel:
// "connector"`, cut 1008-2's P2), so that filing reads N41 joined with N56's words: "… through
// {Name}’s AI." (the orchestrator's Q6, 2026-10-08). The review is the one named on the filing's
// `confirmed` event (`detail.proposal_revision_id`).

import { useQuery } from '@tanstack/react-query';
import { testIdProp } from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import {
  decodeObligationDetail,
  displayLocalDate,
  type ComplianceObligation,
} from './compliance-decode';
import { decodeReview } from './review-decode';
import { useRepinnedLine } from './filing-panel-rule-change';
import { addedFromReview, addedFromReviewThroughAi } from './review-words';

/** The origin the backend gives a filing the organization accepted in a review. */
const FROM_A_REVIEW = 'assessment_result';

/** N41's words for a filing the review put on the calendar (or P2's AI form), else none. */
export function useReviewAddedLine(obligation: ComplianceObligation): string | undefined {
  const { orgRef, operations, personName } = useCompliance();
  const fromReview = obligation.candidateOrigin === FROM_A_REVIEW;
  // The panel's own read of the filing, under its key: the log names the review that confirmed it.
  const detail = useQuery({
    queryKey: ['compliance', 'obligation', orgRef, obligation.obligationId],
    queryFn: () => operations.getObligation(orgRef ?? '', obligation.obligationId),
    enabled: orgRef !== null && fromReview,
  });
  const confirmed = decodeObligationDetail(detail.data)
    .events.filter((event) => event.eventType === 'confirmed')
    .at(-1);
  const revisionId =
    typeof confirmed?.detail.proposal_revision_id === 'string'
      ? confirmed.detail.proposal_revision_id
      : undefined;
  const review = useQuery({
    queryKey: ['compliance', 'review', 'record', orgRef, revisionId],
    queryFn: () => operations.getProposalReview(orgRef ?? '', revisionId ?? ''),
    enabled: orgRef !== null && fromReview && revisionId !== undefined,
  });
  if (!fromReview || confirmed === undefined) return undefined;
  const recorded = decodeReview(review.data);
  // Until the review reads, nothing: a line naming the wrong channel would be worse than a moment
  // without one.
  if (revisionId !== undefined && recorded === undefined) return undefined;
  const day = displayLocalDate(confirmed.occurredAt);
  const name = (userId: string | undefined) =>
    userId !== undefined ? personName?.(userId) : undefined;
  return recorded?.channel === 'connector'
    ? addedFromReviewThroughAi(day, name(confirmed.actorUserId))
    : addedFromReview(day, name(confirmed.actorUserId));
}

/**
 * How the filing came to be as it is, under the panel's title: N41 (the review added it), then N39
 * (who moved it to its rule's updated version, frame 104). One block, its lines 4 px apart, never a
 * section apart (REVIEW-24's U9); nothing at all when neither applies.
 */
export function FilingProvenance({ obligation }: { obligation: ComplianceObligation }) {
  const added = useReviewAddedLine(obligation);
  const repinned = useRepinnedLine(obligation);
  if (added === undefined && repinned === undefined) return null;
  return (
    <div className="flex flex-col gap-4" {...testIdProp('compliance-panel-provenance')}>
      {added !== undefined && (
        <p
          className="text-s13 leading-h15 text-text-secondary"
          {...testIdProp('compliance-panel-from-review')}
        >
          {added}
        </p>
      )}
      {repinned !== undefined && (
        <p
          className="text-s13 leading-h15 text-text-secondary"
          {...testIdProp('compliance-panel-repinned')}
        >
          {repinned}
        </p>
      )}
    </div>
  );
}
